/**
 * Collection filter accessibility layer.
 *
 * Progressive enhancement that sits ALONGSIDE the vendor `<filter-container>` and
 * `<toggle-target>` elements in assets/main.js — it never patches them. It supplies the
 * semantics the vendor markup is missing:
 *
 *   - `aria-expanded` kept in sync with each filter group's real collapsed state
 *   - Space activation for the `role="button"` group triggers (the vendor handles Enter only)
 *   - Escape closes the focused group, or the whole panel, and returns focus to its trigger
 *   - the mobile drawer behaves as a modal dialog: labelled, focus-trapped, scroll-locked,
 *     and `inert` while off-screen. The desktop side pop-out deliberately does none of that
 *   - a polite live region announces the new result count after every AJAX re-render
 *
 * The AJAX re-render is detected with a MutationObserver on `.filters__inner`, because
 * main.js dispatches its `filters:update` event at pushState time — before the fetch
 * resolves — so that event cannot be used as a "content has changed" signal.
 */
(() => {
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const TOGGLE_TARGET_SELECTOR = 'toggle-target[data-toggle-target]';

  /**
   * Whether a keyboard event is the Space key (handles the legacy 'Spacebar' value).
   *
   * @param {KeyboardEvent} evt - The keyboard event to check.
   * @returns {boolean} True when the event is a Space keypress.
   */
  const isSpaceKey = (evt) => evt.key === ' ' || evt.key === 'Spacebar';

  /**
   * Custom element wrapping the vendor `<filter-container>` with the accessibility
   * behaviour described above.
   */
  class TdCollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.panelObservers = [];
      this.wasModalOpen = false;
      this.onDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
      this.onContainerKeyDown = this.handleContainerKeyDown.bind(this);
      this.onContainerKeyUp = this.handleContainerKeyUp.bind(this);
      this.onBreakpointChange = this.applyPanelState.bind(this);
    }

    /**
     * Defers setup to the next animation frame so the rest of the section has parsed.
     *
     * @returns {void}
     */
    connectedCallback() {
      // A re-connect must not stack a second set of observers and listeners.
      this.teardown();
      // The rest of the section may still be parsing when this element upgrades.
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    /**
     * Releases everything setup() registered.
     *
     * @returns {void}
     */
    disconnectedCallback() {
      this.teardown();
    }

    /**
     * Tears down observers, listeners, pending frames/timers and body-level state registered
     * in setup(), and resets instance state so a later setup() starts clean.
     *
     * @returns {void}
     */
    teardown() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.focusFrame);
      window.clearTimeout(this.announceTimer);
      this.teardownPanelObservers();
      if (this.rerenderObserver) this.rerenderObserver.disconnect();
      if (this.containerObserver) this.containerObserver.disconnect();
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      if (this.container) {
        this.container.removeEventListener('keydown', this.onContainerKeyDown);
        this.container.removeEventListener('keyup', this.onContainerKeyUp);
      }
      if (this.desktopQuery) {
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
      }
      document.body.classList.remove('td-filters-scroll-locked');
      this.rerenderObserver = null;
      this.containerObserver = null;
      this.desktopQuery = null;
      this.container = null;
      this.wasModalOpen = false;
    }

    /**
     * Wires the element to its `<filter-container>` host once the DOM is ready.
     *
     * @returns {void}
     */
    setup() {
      this.container = this.closest('filter-container');
      if (!this.container) return;

      this.section = this.container.closest('[data-section-id]') || this.container.parentElement;
      this.panel = this.container.querySelector('.filters' /* td-selector-exception: vendor filter-container markup */);
      this.inner = this.container.querySelector('.filters__inner' /* td-selector-exception: vendor filter-container markup */);
      this.status = this.querySelector('[data-td-filters-status]');
      if (!this.panel || !this.inner) return;

      const minWidth = parseInt(this.dataset.tdDesktopMinWidth, 10) || 1000;
      this.desktopQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      this.syncDisclosures();
      this.applyPanelState();

      this.rerenderObserver = new MutationObserver(() => this.handleRerender());
      this.rerenderObserver.observe(this.inner, { childList: true });

      this.containerObserver = new MutationObserver(() => this.applyPanelState());
      this.containerObserver.observe(this.container, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.container.addEventListener('keydown', this.onContainerKeyDown);
      this.container.addEventListener('keyup', this.onContainerKeyUp);
      document.addEventListener('keydown', this.onDocumentKeyDown);
    }

    /* ----------------------------------------------------------------------
       Open / modal state
       ---------------------------------------------------------------------- */

    /**
     * Whether the current viewport matches the desktop breakpoint.
     *
     * @returns {boolean}
     */
    get isDesktop() {
      return Boolean(this.desktopQuery && this.desktopQuery.matches);
    }

    /**
     * Whether the filter panel is currently shown, accounting for the active breakpoint.
     *
     * @returns {boolean}
     */
    get isOpen() {
      // main.js toggles both classes together, but only one of them is honoured by the
      // stylesheet at a given breakpoint, so the live state is breakpoint dependent.
      const className = this.isDesktop
        ? 'filter-container--show-filters-desktop'
        : 'filter-container--show-filters-mobile';
      return this.container.classList.contains(className);
    }

    /**
     * The primary filter-toggle button for this element's section.
     *
     * @returns {HTMLElement|null}
     */
    get toggleButton() {
      // The utility bar is an AJAX container, so this node is replaced on every update.
      if (!this.section) return null;
      return this.section.querySelector(
        '.utility-bar:not(.utility-bar--sticky-mobile-copy) .toggle-btn[data-toggle-filters]' /* td-selector-exception: vendor utility-bar markup */
      );
    }

    /**
     * All filter-toggle buttons for this section, including the mobile sticky clone.
     *
     * @returns {HTMLElement[]}
     */
    get allToggleButtons() {
      if (!this.section) return [];
      // main.js clones the utility bar for the mobile sticky copy, so there can be two.
      return Array.from(
        this.section.querySelectorAll('.toggle-btn[data-toggle-filters]' /* td-selector-exception: vendor utility-bar markup */)
      );
    }

    /**
     * Syncs ARIA/modal attributes and scroll-lock state with the panel's open/closed state.
     *
     * @returns {void}
     */
    applyPanelState() {
      const modal = !this.isDesktop;
      const open = this.isOpen;
      const panelId = this.dataset.tdFiltersPanel;

      if (modal) {
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        this.panel.setAttribute('tabindex', '-1');
        if (panelId) this.panel.setAttribute('aria-labelledby', `${panelId}-heading`);
      } else {
        this.panel.removeAttribute('role');
        this.panel.removeAttribute('aria-modal');
        this.panel.removeAttribute('tabindex');
        this.panel.removeAttribute('aria-labelledby');
      }

      // The closed mobile drawer is only parked off-screen, so its controls stay tabbable
      // without this. The desktop column is hidden with `visibility`, which is enough.
      if (modal && !open) {
        this.panel.setAttribute('inert', '');
      } else {
        this.panel.removeAttribute('inert');
      }

      document.body.classList.toggle('td-filters-scroll-locked', modal && open);

      const primaryToggle = this.toggleButton;
      this.allToggleButtons.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(open));
        if (toggle === primaryToggle && panelId) {
          toggle.setAttribute('aria-controls', panelId);
        } else {
          // theme.suffixIds() rewrites the clone's aria-controls to a dangling id.
          toggle.removeAttribute('aria-controls');
        }
      });

      // Only on the open transition — re-running this on every AJAX update would steal
      // focus back from wherever main.js restored it.
      const modalOpen = modal && open;
      if (modalOpen && !this.wasModalOpen && !this.panel.contains(document.activeElement)) {
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => {
          const first = this.panel.querySelector(FOCUSABLE_SELECTOR);
          (first || this.panel).focus();
        });
      }
      this.wasModalOpen = modalOpen;
    }

    /**
     * Closes the filter panel via its toggle button, optionally returning focus to it.
     *
     * @param {{returnFocus?: boolean}} options - Whether to refocus the toggle button after closing.
     * @returns {void}
     */
    closePanel({ returnFocus = false } = {}) {
      const toggle = this.toggleButton;
      if (!toggle) return;
      toggle.click();
      if (returnFocus) toggle.focus();
    }

    /* ----------------------------------------------------------------------
       Filter group disclosures
       ---------------------------------------------------------------------- */

    /**
     * Disconnects and clears all per-group disclosure observers.
     *
     * @returns {void}
     */
    teardownPanelObservers() {
      this.panelObservers.forEach((observer) => observer.disconnect());
      this.panelObservers = [];
    }

    /**
     * Re-attaches disclosure state tracking to every filter-group toggle in the panel.
     *
     * @returns {void}
     */
    syncDisclosures() {
      this.teardownPanelObservers();
      this.inner.querySelectorAll(TOGGLE_TARGET_SELECTOR).forEach((trigger) => {
        const group = this.groupPanelFor(trigger);
        if (!group) return;
        this.updateDisclosure(trigger, group);
        const observer = new MutationObserver(() => this.updateDisclosure(trigger, group));
        observer.observe(group, { attributes: true, attributeFilter: ['class'] });
        this.panelObservers.push(observer);
      });
    }

    /**
     * Resolves the DOM node targeted by a toggle-target trigger's data-toggle-target selector.
     *
     * @param {HTMLElement} trigger - The toggle-target element declaring the selector.
     * @returns {Element|null}
     */
    groupPanelFor(trigger) {
      const selector = trigger.dataset.toggleTarget;
      if (!selector) return null;
      try {
        return document.querySelector(selector);
      } catch {
        return null;
      }
    }

    /**
     * Updates a toggle-target trigger's aria-expanded/aria-controls to match its group's
     * collapsed state.
     *
     * @param {HTMLElement} trigger - The toggle-target trigger element.
     * @param {Element} group - The disclosure panel the trigger controls.
     * @returns {void}
     */
    updateDisclosure(trigger, group) {
      const collapsed =
        group.classList.contains('toggle-target--hidden') ||
        group.classList.contains('toggle-target--hiding');
      trigger.setAttribute('aria-expanded', String(!collapsed));
      if (group.id) trigger.setAttribute('aria-controls', group.id);
    }

    /* ----------------------------------------------------------------------
       Keyboard
       ---------------------------------------------------------------------- */

    /**
     * Prevents the page from scrolling when Space is pressed on a toggle-target trigger.
     *
     * @param {KeyboardEvent} evt - The keydown event.
     * @returns {void}
     */
    handleContainerKeyDown(evt) {
      if (!isSpaceKey(evt)) return;
      const trigger = evt.target.closest && evt.target.closest(TOGGLE_TARGET_SELECTOR);
      // Stop the page scrolling; the activation itself happens on keyup, as for a real button.
      if (trigger) evt.preventDefault();
    }

    /**
     * Activates the focused toggle-target trigger on Space keyup, mirroring native button
     * behaviour.
     *
     * @param {KeyboardEvent} evt - The keyup event.
     * @returns {void}
     */
    handleContainerKeyUp(evt) {
      if (!isSpaceKey(evt)) return;
      const trigger = evt.target.closest && evt.target.closest(TOGGLE_TARGET_SELECTOR);
      if (!trigger) return;
      evt.preventDefault();
      trigger.click();
    }

    /**
     * Routes Escape and Tab (focus-trap) handling for the document while the panel is relevant.
     *
     * @param {KeyboardEvent} evt - The keydown event.
     * @returns {void}
     */
    handleDocumentKeyDown(evt) {
      if (evt.key === 'Escape') {
        this.handleEscape(evt);
        return;
      }
      if (evt.key === 'Tab' && !this.isDesktop && this.isOpen) {
        this.trapFocus(evt);
      }
    }

    /**
     * Closes the nearest expanded group, or the whole panel, and restores focus appropriately.
     *
     * @param {KeyboardEvent} evt - The originating Escape keydown event.
     * @returns {void}
     */
    handleEscape(evt) {
      const target = evt.target;
      if (this.container.contains(target)) {
        const trigger = this.expandedTriggerFor(target);
        if (trigger) {
          evt.preventDefault();
          trigger.click();
          trigger.focus();
          return;
        }
      }

      if (this.isOpen && (this.container.contains(target) || !this.isDesktop)) {
        evt.preventDefault();
        this.closePanel({ returnFocus: true });
      }
    }

    /**
     * The expanded group that owns `node`, whether focus is inside the group's panel or on
     * the trigger itself. Returns null when nothing is open around it.
     *
     * @param {Node} node - The node to resolve an expanded group for.
     * @returns {Element|null}
     */
    expandedTriggerFor(node) {
      if (!node.closest) return null;
      const group = node.closest('.filter-group' /* td-selector-exception: vendor filter-group markup */);
      if (!group) return null;
      const trigger = group.querySelector(TOGGLE_TARGET_SELECTOR);
      if (!trigger || trigger.getAttribute('aria-expanded') !== 'true') return null;
      return trigger;
    }

    /**
     * The panel's Tab stops in order. A radio group is one stop — its checked radio, or its
     * first when none is checked — so the sort group's other radios must not count, or the trap
     * waits on a "last" control Tab never reaches and focus leaves from the real one.
     *
     * @returns {HTMLElement[]}
     */
    tabStops() {
      const stops = [];
      const radioGroups = new Map();
      this.panel.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
        if (el.offsetParent === null && el !== document.activeElement) return;
        if (el.type === 'radio' && el.name) {
          if (radioGroups.has(el.name)) {
            if (el.checked) stops[radioGroups.get(el.name)] = el;
            return;
          }
          radioGroups.set(el.name, stops.length);
        }
        stops.push(el);
      });
      return stops;
    }

    /**
     * Implements a focus trap cycling between the panel's first and last Tab stops.
     *
     * @param {KeyboardEvent} evt - The Tab keydown event.
     * @returns {void}
     */
    trapFocus(evt) {
      const focusable = this.tabStops();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!this.panel.contains(active)) {
        evt.preventDefault();
        (evt.shiftKey ? last : first).focus();
        return;
      }
      if (evt.shiftKey && active === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && active === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    /* ----------------------------------------------------------------------
       AJAX re-render
       ---------------------------------------------------------------------- */

    /**
     * Re-syncs disclosure state, panel attributes and the live region after an AJAX re-render.
     *
     * @returns {void}
     */
    handleRerender() {
      this.syncDisclosures();
      this.applyPanelState();
      this.announceResults();
    }

    /**
     * Announces on every re-render, including one that happens to return the same count —
     * "still 24 products" is the feedback a screen reader user needs most.
     *
     * @returns {void}
     */
    announceResults() {
      if (!this.status) return;
      const source = this.inner.querySelector('[data-td-filters-results]');
      if (!source) return;
      const text = source.textContent.trim();
      if (!text) return;
      window.clearTimeout(this.announceTimer);
      // Clear first so assistive tech reliably re-reads an unchanged string.
      this.status.textContent = '';
      this.announceTimer = window.setTimeout(() => {
        this.status.textContent = text;
      }, 80);
    }
  }

  if (!customElements.get('td-collection-filters')) {
    customElements.define('td-collection-filters', TdCollectionFilters);
  }
})();
